import clsx from "clsx";
import {
  ChangeEvent,
  FC,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Tooltip } from "react-tooltip";
import { useGlobalModalContext } from "../../context/GlobalModal";
import { useTranslation } from "../../i18n";
import type { V2PrivacyControls } from "../../privacy";
import { PrivacyGhost, useHeaderGhostMotion } from "./PrivacyGhost";
import { PrivateModeLogin } from "./UnlockPrivateMode";
import {
  PrivateModeProgress,
  privateModeProgressText,
} from "./PrivateModeProgress";
import { Button } from "../buttons/Button";
import { Input } from "../inputs/Input";
import { Checkbox } from "../inputs/Checkbox";
import { Eye, EyeSlash, Xmark } from "../icons/Icons";
import { RecoveryPassphraseActions } from "./RecoveryPassphraseActions";
import { generateRecoveryPassphrase } from "./recoveryPassphrase";
import {
  prepareRecoveryBackup,
  type PreparedRecoveryBackup,
} from "./recoveryBackupExport";
import { RecoveryBackupDelivery } from "./RecoveryBackupDelivery";
import { PrivateSessionSettings } from "./PrivateSessionSettings";
import "../../scss/private_mode_access.scss";

export const PrivateModeRecoveryPanel: FC<{
  controls: V2PrivacyControls;
  close: () => void;
  initialView?: PrivateModeRecoveryView;
  returnToAccount?: boolean;
  loginVariant?: "default" | "session-expired";
}> = ({
  controls,
  close,
  initialView = "login",
  returnToAccount = false,
  loginVariant = "default",
}) => {
  const { t: tRedacted } = useTranslation("redacted");
  const [passphrase, setPassphrase] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [recovery, setRecovery] = useState("");
  const [preparedBackup, setPreparedBackup] =
    useState<PreparedRecoveryBackup | null>(null);
  const preparedIdentity = useRef<{
    backup: PreparedRecoveryBackup;
    accountId?: string;
  } | null>(null);
  useLayoutEffect(() => {
    // Creation publishes its new identity before the prepared-file render.
    if (!preparedBackup) preparedIdentity.current = null;
    else if (preparedIdentity.current?.backup !== preparedBackup)
      preparedIdentity.current = {
        backup: preparedBackup,
        accountId: controls.activeAccountId,
      };
  }, [preparedBackup, controls.activeAccountId]);
  const backupPrepared = !!preparedBackup;
  const [passphraseSaved, setPassphraseSaved] = useState(false);
  const [pasteBackup, setPasteBackup] = useState(false);
  const [working, setWorking] = useState(false);
  const inFlight = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const fileReadGeneration = useRef(0);
  const mounted = useRef(true);
  const latestAccountId = useRef(controls.activeAccountId);
  useLayoutEffect(() => {
    latestAccountId.current = controls.activeAccountId;
  }, [controls.activeAccountId]);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      fileReadGeneration.current += 1;
    };
  }, []);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [view, setView] = useState<PrivateModeRecoveryView>(initialView);
  const exportAccountId = useRef(controls.activeAccountId);
  // Keep the entry destination even if a restore changes the live session state.
  const openedFromAccount = useRef(
    returnToAccount && controls.status === "ready" && !!controls.activeAccountId
  ).current;
  const exportAccountChanged =
    (view === "export" &&
      exportAccountId.current !== controls.activeAccountId) ||
    (!!preparedBackup &&
      preparedIdentity.current?.backup === preparedBackup &&
      preparedIdentity.current.accountId !== controls.activeAccountId);
  const pendingBackup =
    view !== "export" && (controls.recoveryExportRequired || backupPrepared);
  const busy =
    working ||
    ["unlocking", "onboarding", "processing"].includes(controls.status);
  const currentView = pendingBackup ? "create" : view;
  const awaitingContinuation =
    currentView === "login" &&
    controls.progress?.phase === "wallet-signature-ready" &&
    !!controls.continueWalletUnlock &&
    !!controls.cancelWalletUnlock;
  const passphrasesMatch =
    passphrase.length >= 14 && passphrase === confirmation;
  const passphraseTooShort = passphrase.length > 0 && passphrase.length < 14;
  const confirmationMismatch =
    confirmation.length > 0 && passphrase !== confirmation;
  const progressLabel =
    controls.progress && busy
      ? privateModeProgressText(controls.progress).label
      : undefined;

  useEffect(() => {
    if (busy || exportAccountChanged) setShowPassphrase(false);
  }, [busy, exportAccountChanged]);

  const changeView = (next: typeof view) => {
    fileReadGeneration.current += 1;
    setShowPassphrase(false);
    setPassphrase("");
    setConfirmation("");
    setRecovery("");
    setFileName("");
    setPasteBackup(false);
    setPassphraseSaved(false);
    setPreparedBackup(null);
    setError("");
    setView(next);
  };

  const run = async (
    operation: () => Promise<void>,
    closeAfter = true,
    fallback = "Could not complete this action. Please try again."
  ) => {
    if (inFlight.current || busy || exportAccountChanged) return;
    inFlight.current = true;
    setWorking(true);
    setError("");
    try {
      await operation();
      if (closeAfter) close();
    } catch (error) {
      const message =
        typeof error === "object" && error !== null && "message" in error
          ? String(error.message ?? "").trim()
          : typeof error === "string"
            ? error.trim()
            : "";
      setError(message && message !== "OperationError" ? message : fallback);
    } finally {
      setWorking(false);
      inFlight.current = false;
    }
  };

  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const generation = ++fileReadGeneration.current;
    const file = event.target.files?.[0];
    setRecovery("");
    setFileName("");
    setError("");
    if (!file) return;
    try {
      if (file.size > 1024 * 1024)
        throw new Error("Recovery files must be smaller than 1 MB.");
      const contents = await file.text();
      if (!mounted.current || generation !== fileReadGeneration.current) return;
      setRecovery(contents);
      setFileName(file.name);
    } catch {
      if (!mounted.current || generation !== fileReadGeneration.current) return;
      setError(
        "Could not read this recovery file. Choose a backup smaller than 1 MB."
      );
    }
  };

  return (
    <section
      className="private-mode-access"
      role="dialog"
      aria-modal="true"
      aria-label="Private Mode">
      <button
        className="transparent modal__close fs-12 color-white fw-600 flex ai-c mr-1 mb-1 hover-primary1"
        disabled={busy && !awaitingContinuation}
        onClick={() => {
          if (awaitingContinuation) controls.cancelWalletUnlock?.();
          close();
        }}>
        <Xmark className="w-2 h-a mr-1" />
        Close
      </button>
      {currentView === "login" ? (
        <>
          <PrivateModeLogin
            variant={loginVariant}
            isSigning={busy}
            progress={controls.progress}
            busyLabel="Preparing Private Mode..."
            onUnlock={() => run(controls.activateWithWallet)}
            onContinue={
              awaitingContinuation ? controls.continueWalletUnlock : undefined
            }
          />
          {/* Device preferences: usable while the wallet signature is
              awaited; locked only while an account is created or a
              transaction runs. */}
          <PrivateSessionSettings
            controls={controls}
            disabled={["onboarding", "processing"].includes(controls.status)}
            placement="corner"
          />
          <div className="private-mode-access__secondary">
            <Button
              className="transparent private-mode-access__link"
              label={tRedacted("restorePrivateAccount")}
              disabled={busy}
              onClick={() => changeView("import")}
            />
          </div>
        </>
      ) : (
        <div className="private-mode-access__content">
          <h2>
            {currentView === "options"
              ? "Account options"
              : currentView === "import"
                ? tRedacted("restorePrivateAccount")
                : tRedacted("backupPrivateAccount")}
          </h2>
          <p className="private-mode-access__description">
            {currentView === "options"
              ? "Create another account or import one from a backup."
              : currentView === "import"
                ? "Choose your encrypted backup and enter its passphrase."
                : currentView === "export"
                  ? "Keep access to your Reserve, Spending and positions."
                  : "Keep your backup file and passphrase separately. You need both to reopen this account."}
          </p>
          {currentView === "options" ? (
            <>
              <Button
                className="button--full button--wrap-label"
                label="Create another account"
                disabled={busy}
                onClick={() => changeView("create")}
              />
              <Button
                className="button--outline button--grey button--full button--wrap-label"
                label="Import account"
                disabled={busy}
                onClick={() => changeView("import")}
              />
              <p className="private-mode-access__hint">
                Switch between accounts in the wallet menu. Keep a backup for
                each account.
              </p>
            </>
          ) : (
            <>
              {currentView === "import" && (
                <>
                  <input
                    ref={fileInput}
                    type="file"
                    hidden
                    accept="application/json,text/plain,.json,.txt"
                    aria-label="Recovery file"
                    disabled={busy}
                    onChange={readFile}
                  />
                  <Button
                    className="button--outline button--grey button--full button--wrap-label"
                    label="Choose recovery file"
                    disabled={busy}
                    onClick={() => {
                      fileReadGeneration.current += 1;
                      setRecovery("");
                      setFileName("");
                      setError("");
                      setPasteBackup(false);
                      fileInput.current?.click();
                    }}
                  />
                  <Button
                    className="transparent private-mode-access__link"
                    label={
                      pasteBackup
                        ? "Use a backup file"
                        : "Paste encrypted backup instead"
                    }
                    disabled={busy}
                    onClick={() => {
                      fileReadGeneration.current += 1;
                      setRecovery("");
                      setFileName("");
                      setError("");
                      setPasteBackup((value) => !value);
                    }}
                  />
                  {pasteBackup && (
                    <textarea
                      className="private-mode-access__backup-text"
                      aria-label="Encrypted backup text"
                      placeholder="Paste your encrypted backup"
                      value={recovery}
                      disabled={busy}
                      spellCheck={false}
                      autoCapitalize="none"
                      autoComplete="off"
                      onChange={(event) => {
                        fileReadGeneration.current += 1;
                        const value = event.target.value;
                        if (new Blob([value]).size > 1024 * 1024) {
                          setRecovery("");
                          setError(
                            "Recovery backups must be smaller than 1 MB."
                          );
                          return;
                        }
                        setRecovery(value);
                        setFileName("");
                        setError("");
                      }}
                    />
                  )}
                  {fileName && (
                    <p className="private-mode-access__filename">{fileName}</p>
                  )}
                </>
              )}
              {currentView !== "import" && (
                <p className="private-mode-access__step">
                  {preparedBackup
                    ? "2. Save your backup"
                    : "1. Save your passphrase"}
                </p>
              )}
              <details
                key={preparedBackup ? "prepared" : "entering"}
                className="private-mode-access__passphrase-review"
                open={!preparedBackup}>
                <summary hidden={!preparedBackup}>
                  {preparedBackup ? "Review passphrase" : ""}
                </summary>
                <div className="private-mode-access__passphrase-fields">
                  <Input
                    id="private-recovery-passphrase"
                    label="Recovery passphrase"
                    type={showPassphrase ? "text" : "password"}
                    containerClassName="private-mode-access__passphrase"
                    autoComplete={
                      currentView === "import"
                        ? "current-password"
                        : "new-password"
                    }
                    disabled={busy || backupPrepared || exportAccountChanged}
                    value={passphrase}
                    onChange={(event) => {
                      setPassphrase(event.target.value);
                      setPassphraseSaved(false);
                    }}
                    spellCheck={false}
                    autoCapitalize="none"
                    aria-invalid={passphraseTooShort}
                    aria-describedby={
                      passphraseTooShort
                        ? "private-recovery-passphrase-length"
                        : undefined
                    }>
                    <button
                      type="button"
                      className="transparent private-mode-access__reveal color-grey hover-white"
                      aria-label={
                        showPassphrase ? "Hide passphrase" : "Show passphrase"
                      }
                      aria-pressed={showPassphrase}
                      aria-controls={
                        currentView === "import"
                          ? "private-recovery-passphrase"
                          : "private-recovery-passphrase private-recovery-confirmation"
                      }
                      disabled={busy || exportAccountChanged}
                      onClick={() => setShowPassphrase((shown) => !shown)}>
                      {showPassphrase ? (
                        <EyeSlash className="w-2 h-2" aria-hidden="true" />
                      ) : (
                        <Eye className="w-2 h-2" aria-hidden="true" />
                      )}
                    </button>
                  </Input>
                  {(currentView === "create" || currentView === "export") && (
                    <RecoveryPassphraseActions
                      passphrase={passphrase}
                      accountId={controls.activeAccountId}
                      view={currentView}
                      disabled={busy || exportAccountChanged}
                      generationDisabled={backupPrepared}
                      onGenerate={() => {
                        if (busy || backupPrepared || exportAccountChanged)
                          return;
                        setError("");
                        try {
                          const generated = generateRecoveryPassphrase();
                          setPassphrase(generated);
                          setConfirmation("");
                          setPassphraseSaved(false);
                          setShowPassphrase(false);
                        } catch (error) {
                          setError((error as Error).message);
                        }
                      }}
                    />
                  )}
                  {currentView !== "import" && (
                    <Input
                      id="private-recovery-confirmation"
                      label="Confirm passphrase"
                      type={showPassphrase ? "text" : "password"}
                      autoComplete="new-password"
                      disabled={busy || backupPrepared || exportAccountChanged}
                      value={confirmation}
                      onChange={(event) => setConfirmation(event.target.value)}
                      aria-invalid={confirmationMismatch}
                      aria-describedby={
                        confirmationMismatch
                          ? "private-recovery-mismatch"
                          : undefined
                      }
                      spellCheck={false}
                      autoCapitalize="none"
                    />
                  )}
                  <p className="private-mode-access__hint">
                    {currentView === "import"
                      ? "At least 14 characters. Your passphrase stays in this browser."
                      : "Use 14+ characters. Save in a password manager, separate from your backup."}
                  </p>
                  {passphraseTooShort && (
                    <p
                      id="private-recovery-passphrase-length"
                      className="private-mode-access__hint private-mode-access__mismatch"
                      role="status">
                      Use at least 14 characters.
                    </p>
                  )}
                  {currentView !== "import" && confirmationMismatch && (
                    <p
                      id="private-recovery-mismatch"
                      className="private-mode-access__hint private-mode-access__mismatch"
                      role="status">
                      Passphrases do not match.
                    </p>
                  )}
                </div>
              </details>
              {currentView === "import" ? (
                <Button
                  className={clsx(
                    "button--full button--wrap-label",
                    busy && "button--waiting"
                  )}
                  aria-busy={busy}
                  label={
                    progressLabel ??
                    (busy ? "Recovering..." : "Restore and unlock")
                  }
                  disabled={busy || !recovery || passphrase.length < 14}
                  onClick={() =>
                    run(
                      async () => {
                        await controls.importRecovery(recovery, passphrase);
                        await controls.onboardCurrent();
                      },
                      true,
                      "Could not open this backup. Check the file and passphrase."
                    )
                  }
                />
              ) : (
                <>
                  {!backupPrepared && (
                    <>
                      <Checkbox
                        label="I saved my passphrase."
                        labelClassName="condensed"
                        checked={passphraseSaved}
                        disabled={
                          busy || !passphrasesMatch || exportAccountChanged
                        }
                        onChange={(event) =>
                          setPassphraseSaved(event.target.checked)
                        }
                      />
                      <Button
                        className="button--full button--wrap-label"
                        label={
                          working ? "Preparing backup..." : "Prepare backup"
                        }
                        disabled={
                          busy ||
                          !passphrasesMatch ||
                          !passphraseSaved ||
                          exportAccountChanged
                        }
                        onClick={() =>
                          run(async () => {
                            if (!passphrasesMatch || !passphraseSaved) return;
                            const accountId = controls.activeAccountId;
                            // Resume a pending backup using the same key, never replace it.
                            const value =
                              currentView === "export" ||
                              controls.recoveryExportRequired
                                ? await controls.exportRecovery(passphrase)
                                : await controls.createRandomRecovery(
                                    passphrase
                                  );
                            if (!mounted.current) return;
                            if (
                              currentView === "export" &&
                              latestAccountId.current !== accountId
                            )
                              throw new Error(
                                "The active account changed. Reopen its backup options."
                              );
                            setPreparedBackup(prepareRecoveryBackup(value));
                            setShowPassphrase(false);
                          }, false)
                        }
                      />
                    </>
                  )}
                  {preparedBackup && (
                    <RecoveryBackupDelivery
                      backup={preparedBackup}
                      accountId={controls.activeAccountId}
                      disabled={busy || exportAccountChanged}
                      doneLabel={
                        currentView === "export"
                          ? "Done"
                          : (progressLabel ??
                            (busy
                              ? "Creating account..."
                              : "Confirm and continue"))
                      }
                      onDone={() =>
                        run(async () => {
                          if (currentView === "export") return;
                          controls.confirmRecoveryExport();
                          await controls.onboardCurrent();
                        })
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
          {busy && controls.progress && (
            <PrivateModeProgress progress={controls.progress} />
          )}
          {!pendingBackup && currentView !== "export" && (
            <Button
              className="transparent private-mode-access__link"
              label={
                openedFromAccount ? "Back to account" : "Back to wallet unlock"
              }
              disabled={busy}
              onClick={() =>
                openedFromAccount ? close() : changeView("login")
              }
            />
          )}
        </div>
      )}
      <div className="private-mode-access__feedback" aria-live="polite">
        {(error || controls.error || exportAccountChanged) && (
          <p role="alert">
            {error ||
              controls.error ||
              "The active account changed. Reopen its backup options."}
          </p>
        )}
      </div>
    </section>
  );
};

// GlobalModal stores its child element. Subscribe here so an open modal receives
// session changes (especially the mandatory recovery-export gate).
export type PrivateModeRecoveryView =
  | "login"
  | "options"
  | "import"
  | "create"
  | "export";

export const createPrivateControlsStore = (initial?: V2PrivacyControls) => {
  let current = initial;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => current,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    update: (next?: V2PrivacyControls) => {
      if (current !== next) {
        current = next;
        listeners.forEach((listener) => listener());
      }
    },
  };
};

const LiveRecoveryPanel: FC<{
  store: ReturnType<typeof createPrivateControlsStore>;
  close: () => void;
  initialView: PrivateModeRecoveryView;
  returnToAccount: boolean;
  loginVariant?: "default" | "session-expired";
}> = ({ store, close, initialView, returnToAccount, loginVariant }) => {
  const controls = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );
  if (!controls) return null;
  return (
    <PrivateModeRecoveryPanel
      controls={controls}
      close={close}
      initialView={initialView}
      returnToAccount={returnToAccount}
      loginVariant={loginVariant}
    />
  );
};

export const usePrivateModeRecoveryModal = (controls?: V2PrivacyControls) => {
  const { showModal, showModalIfClosed, hideModal } = useGlobalModalContext();
  const [store] = useState(() => createPrivateControlsStore(controls));
  useLayoutEffect(() => store.update(controls), [controls, store]);
  return (
    initialView: PrivateModeRecoveryView = "login",
    returnToAccount = false,
    options: {
      onlyIfClosed?: boolean;
      loginVariant?: "default" | "session-expired";
    } = {}
  ) => {
    if (!store.getSnapshot()) return false;
    const modal = {
      title: "",
      backgroundClose: false,
      hideCloseButton: true,
      // Signing in, recovering or creating an account changes the account.
      keepOnAccountChange: true,
      className: "modal--unlock-private",
      children: (
        <LiveRecoveryPanel
          store={store}
          close={hideModal}
          initialView={initialView}
          returnToAccount={returnToAccount}
          loginVariant={options.loginVariant}
        />
      ),
    };
    if (options.onlyIfClosed) return showModalIfClosed(modal);
    showModal(modal);
    return true;
  };
};

export const PrivateModeToggleV2: FC<{
  controls: V2PrivacyControls;
  isPrivateMode?: boolean;
  hasAccounts: boolean;
}> = ({ controls, isPrivateMode, hasAccounts }) => {
  const { t: tRedacted } = useTranslation("redacted");
  const open = usePrivateModeRecoveryModal(controls);
  const [showTooltip, setShowTooltip] = useState(false);
  const ghost = useHeaderGhostMotion(!!isPrivateMode);
  if (!hasAccounts) return null;

  return (
    <>
      <button
        className={clsx("transparent rujira-header__private condensed", {
          "rujira-header__private--active": isPrivateMode,
        })}
        aria-label={
          controls.privateSessionLocked ? "Unlock Private Mode" : "Private Mode"
        }
        aria-pressed={!!isPrivateMode}
        data-session-state={controls.status}
        data-tooltip-id="private-mode-tip"
        data-tooltip-content={
          controls.privateSessionLocked
            ? tRedacted("signInPrivateAccount")
            : isPrivateMode
              ? tRedacted("switchToPublicAccount")
              : ["ready", "processing"].includes(controls.status)
                ? tRedacted("switchToPrivateAccount")
                : tRedacted("signInPrivateAccount")
        }
        onPointerEnter={(event) => {
          setShowTooltip(event.pointerType === "mouse");
          ghost.setHover(event.pointerType === "mouse");
        }}
        onPointerLeave={() => {
          setShowTooltip(false);
          ghost.setHover(false);
        }}
        onPointerDown={() => setShowTooltip(false)}
        onBlur={() => setShowTooltip(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setShowTooltip(false);
        }}
        onClick={() => {
          setShowTooltip(false);
          if (controls.privateSessionLocked) open();
          else if (isPrivateMode) (controls.showPublic ?? controls.lock)();
          else if (
            controls.showPrivate &&
            ["ready", "processing"].includes(controls.status)
          )
            controls.showPrivate();
          else open();
        }}>
        <PrivacyGhost motion={ghost.motion} />
      </button>
      <Tooltip
        id="private-mode-tip"
        className="tooltip"
        imperativeModeOnly
        isOpen={showTooltip}
      />
    </>
  );
};
